import { pick } from 'lodash';
import { useRef } from 'react';
import {
    exportLayerOriginInfo,
    getBehavior,
    getOperateDirection,
    getTrackFunc,
    updateLayerScale,
    useFramework,
    useIdentity,
} from '@/framework';
import { ITouchKitEvent } from '@/utils/event-kit';
import { translatePointAngle } from '@/utils/math-kit';
import { asyncTraverseTree } from '@/utils/tree-kit';
import {
    BaseLayerViewType,
    DirectionType,
    REFERENCE_LINE_EVENT_NAMES,
    SCALE_ABSORB_DISTANCE,
    ScaleOperateType,
    SMART_GUIDE_EVENT_NAMES,
    type ILayerProps,
} from '@/version-types';
import { useScaleProxy } from './use-scale-proxy';

export const useScaleOperate = () => {
    const framework = useFramework();
    const identity = useIdentity();
    const oldInfo = useRef<ILayerProps>();
    const originLayerInfo = useRef<ReturnType<typeof exportLayerOriginInfo>>();

    const { proxyScaling, proxyBeforeScaling, proxyAfterScaling } = useScaleProxy();
    return {
        beforeScale(layerId: string, operateType: ScaleOperateType) {
            const layer = framework.getLayer(layerId, true);
            oldInfo.current = layer;
            originLayerInfo.current = exportLayerOriginInfo(identity, layerId);
            framework.events.emitBeforeBaseOperate(layer, BaseLayerViewType.SCALE);
            if (SCALE_ABSORB_DISTANCE) {
                proxyBeforeScaling(operateType);
            }
        },

        /**
         * 缩放
         *
         * @param id - ID
         * @param e - 可选；触摸事件对象
         * @param keepRatio - 可选；是否保持比例
         */
        scale(id: string, rotate: number, e: ITouchKitEvent, keepRatio: boolean) {
            if (!oldInfo.current || !originLayerInfo.current) return;
            if (!framework.getLayer(id)) return;

            framework.operate.setIsBaseOperate(true);
            const scale = framework.stage.getScale();
            const operateType = getOperateDirection(e.target) as ScaleOperateType;
            const directionX =
                operateType === ScaleOperateType.RB || operateType === ScaleOperateType.RT
                    ? DirectionType.POSITIVE
                    : DirectionType.OPPOSITE;
            const directionY =
                operateType === ScaleOperateType.RB || operateType === ScaleOperateType.LB
                    ? DirectionType.POSITIVE
                    : DirectionType.OPPOSITE;
            const reverse = operateType === ScaleOperateType.RT || operateType === ScaleOperateType.LB;

            const startPoint = translatePointAngle(e.startPoint, -rotate);
            const currentPoint = translatePointAngle(e.currentPoint, -rotate);
            const { childOriginInfoMap, originInfo } = originLayerInfo.current;

            const offsetX = ((currentPoint.x - startPoint.x) / scale) * directionX;
            const offsetY = ((currentPoint.y - startPoint.y) / scale) * directionY;

            // const disableScaleAbsorb =
            //     getBehavior(identity, oldInfo.current)?.disableScaleAbsorb?.(oldInfo.current) ?? false;

            // 吸附
            // if (SCALE_ABSORB_DISTANCE && !disableScaleAbsorb) {
            //     // 先执行一遍, 获取当前拖动对应的图层宽高
            //     const { width, height } = scaleLayer(
            //         identity,
            //         originInfo,
            //         childOriginInfoMap,
            //         id,
            //         offsetX,
            //         offsetY,
            //         pick(oldInfo.current!, ['width', 'height']),
            //         directionX,
            //         reverse,
            //         e.isStart,
            //         {
            //             isStart: e.isStart,
            //             isEnd: e.isEnd,
            //         },
            //         keepRatio,
            //     );
            //     const proxyResult = proxyScaling({
            //         offsetX,
            //         offsetY,
            //         displacementX: width - oldInfo.current.width,
            //         displacementY: height - oldInfo.current.height,
            //         directionX,
            //         directionY,
            //         oldLayer: oldInfo.current,
            //         operateType,
            //         keepRatio,
            //         e,
            //     });
            //     offsetX = proxyResult.x;
            //     offsetY = proxyResult.y;
            // }

            const { width, height, isMin } = updateLayerScale(
                identity,
                id,
                offsetX,
                offsetY,
                pick(oldInfo.current!, ['width', 'height']),
                directionX,
                reverse,
                originInfo,
                childOriginInfoMap,
                {
                    isStart: e.isStart,
                    isEnd: e.isEnd,
                },
                keepRatio,
            );
            framework.operate.cursorTip.set({
                type: 'resize',
                x: e.x,
                y: e.y,
                width,
                height,
                isMin,
            });

            if (SCALE_ABSORB_DISTANCE) {
                proxyAfterScaling(e);
            }

            if (e.isEnd) {
                // if (oldInfo.current) {
                //     framework.history.write(oldInfo.current, getTrackFunc(identity, oldInfo.current, 'scale'));
                // }
                framework.operate.cursorTip.clear();
                const oldLayer = oldInfo.current;
                if (oldLayer) {
                    let hasEffect = false;
                    asyncTraverseTree(oldLayer, async (layer) => {
                        const result = await getBehavior(identity, layer)?.onBaseScaleEnd?.(layer.id);
                        if (result && !hasEffect) {
                            hasEffect = true;
                        }
                    }).then(() => {
                        if (hasEffect) {
                            const layer = framework.getLayer(id, true);
                            if (layer) {
                                framework.stage.addOrUpdate(layer);
                            }
                        }
                        framework.history.write(oldLayer, getTrackFunc(identity, oldLayer, 'scale'));
                    });
                }
                framework.operate.setIsBaseOperate(false);
                framework.stage.addOrUpdate(framework.getLayer(id, true));
                // 清除智能辅助线
                framework.events.emit(SMART_GUIDE_EVENT_NAMES.SET_BLOCKS, []);
                framework.events.emit(SMART_GUIDE_EVENT_NAMES.SET_LINES, []);
                framework.events.emit(REFERENCE_LINE_EVENT_NAMES.HIGHLIGHT, []);
                framework.operate.setActiveLayer(id);
            }
        },
    };
};
